import React, { useEffect, useRef, useState } from 'react';
import classes from './MediaItemDraggable.module.scss';
import { useBreakpoints } from '../../../hooks/useBreakpoints';
import { BreakpointsEnum } from '../../../constants/breakpoints';
import classNames from 'classnames';
import { useDrag, useDrop } from 'react-dnd';
import { Identifier } from 'dnd-core';
import { OnMediaDragHandler } from '../MediaBlock/MediaBlock.view';
import { MediaItem } from '../MediaItem';
import { MediaItemViewProps } from '../MediaItem/MediaItem.view';
import { getEmptyImage } from 'react-dnd-html5-backend';
import { DraggableTypesObject } from '../../../constants/drag-and-drop';

export type MediaDragItem = {
  index: number;
  mediaItemProps: Omit<MediaItemViewProps, 'droppable'>;
  isSmall: boolean;
};

export type MediaItemDraggableViewProps = {
  index: number;
  canDrag: boolean;
  onDragHandler: OnMediaDragHandler;
  mediaItemProps: Omit<MediaItemViewProps, 'droppable'>;
};

export const MediaItemDraggableView = ({
  index,
  canDrag,
  onDragHandler,
  mediaItemProps
}: MediaItemDraggableViewProps): JSX.Element => {
  const ref = useRef<HTMLDivElement>(null);
  const breakpoint = useBreakpoints();
  const [isHovering, setIsHovering] = useState<boolean>(false);
  const isSmall = breakpoint === BreakpointsEnum.small;

  const [{ handlerId, isOver, canDrop }, drop] = useDrop<
    MediaDragItem,
    void,
    { handlerId: Identifier | null; isOver: boolean; canDrop: boolean }
  >({
    accept: DraggableTypesObject.POST_EDITOR_MEDIA,
    collect(monitor) {
      return {
        handlerId: monitor.getHandlerId(),
        isOver: monitor.isOver(),
        canDrop: monitor.canDrop()
      };
    },
    hover(item: MediaDragItem, monitor) {
      if (!ref.current) {
        return;
      }
      const dragIndex = item.index;
      const hoverIndex = index;

      if (dragIndex === hoverIndex) {
        return;
      }

      const hoverBoundingRect = ref.current?.getBoundingClientRect();

      const clientOffset = monitor.getClientOffset();

      if (clientOffset && Math.abs(clientOffset.x - hoverBoundingRect.left) > hoverBoundingRect.width / 1.05) return;

      onDragHandler({ dragIndex, hoverIndex });
      setIsHovering(true);

      item.index = hoverIndex;
    }
  });

  const [, drag, preview] = useDrag<MediaDragItem, MediaDragItem, { isDragging: boolean }>({
    type: DraggableTypesObject.POST_EDITOR_MEDIA,
    item: () => {
      return { index, mediaItemProps, isSmall };
    }
  });

  canDrag && drag(drop(ref));

  useEffect(() => {
    if (!isOver || !canDrop) {
      setIsHovering(false);
    }
  }, [isOver, canDrop]);

  useEffect(() => {
    preview(getEmptyImage(), { captureDraggingState: false });
  }, [preview]);

  const itemClass = classNames(
    classes.Item,
    isSmall ? classes.ItemWrapperSmall : classes.ItemWrapperNormal,
    isHovering ? classes.Dragging : '',
    classes.Hover
  );
  return (
    <div ref={ref} className={itemClass} data-handler-id={handlerId}>
      <MediaItem {...mediaItemProps} droppable={isHovering} />
    </div>
  );
};
